import { MainLayout } from '@/shared/layouts/ui/MainLayout';
import { Button } from '@/shared/ui/Button';
import { Typography } from '@/shared/ui/Typography/Typography';
import LinkArrow from '@/assets/icons/linkArrow.svg?react';
import { VerticalSlider } from '@/widgets/slider/ui/VerticalSlider';
import { useAppSelector } from '@/app/store/hooks';
import { Feedback } from '@/features/feedback/ui/Feedback';
import {
  selectContentError,
  selectContentLoading,
  selectHomePage,
} from '@/entities/home_content/model/selectors';
import React, { useEffect } from 'react';
import {
  selectAllBuildings,
  selectBuildingsInProcess,
} from '@/entities/knowledge/model/selectors';
import { StateMessage } from '@/shared/ui/StateMessage';
import { fetchContent } from '@/entities/home_content/api/fetchSiteContent';
import { fetchBuildings } from '@/entities/knowledge/api/fetchKnowledge';
import { useAppDispatch } from '@/app/store/hooks';
import { KnowledgeCategoryCard } from '@/shared/ui/Card/KnowledgeCategoryCard';

import { Map } from '@/widgets/map/ui/Map';
import { TakePresentation } from '@/widgets/take_presentation/ui/TakePresentation';
import { openFeedBackModal } from '@/shared/ui/Modal/model/slice';
import { useNavigate } from 'react-router-dom';

export default function MainPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const homePageData = useAppSelector(selectHomePage);
  const buildingsInProcess = useAppSelector(selectBuildingsInProcess);
  const isLoading = useAppSelector(selectContentLoading);
  const error = useAppSelector(selectContentError);
  const buildings = useAppSelector(selectAllBuildings);

  const handleShowSelectionDownloadModalWindow = (link: string) => {
    dispatch(
      openFeedBackModal({
        type: 'selection',
        link: link,
      })
    );
  };

  const handleBuildingOpen = (id?: string) => {
    navigate(id ? `/map?direction=${id}` : '/map');
  };

  // Ensure data is loaded when component mounts
  useEffect(() => {
    if (!homePageData && !isLoading && !error) {
      dispatch(fetchContent());
    }
    if (buildingsInProcess.length === 0 && !isLoading && !error) {
      dispatch(fetchBuildings());
    }
  }, [
    dispatch,
    homePageData,
    buildingsInProcess.length,
    buildings,
    isLoading,
    error,
  ]);

  if (isLoading) {
    return (
      <MainLayout>
        <StateMessage
          title="Загружаем данные"
          message="Пожалуйста, подождите — мы получаем данные с сервера."
        />
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <StateMessage title="Не удалось загрузить данные" message={error} />
      </MainLayout>
    );
  }

  // Show loading if data is not available and we're not in an error state
  if (!homePageData && !buildings && !error) {
    return (
      <MainLayout>
        <StateMessage
          title="Загружаем данные"
          message="Пожалуйста, подождите — мы получаем данные с сервера."
        />
      </MainLayout>
    );
  }

  // Only show "Данные недоступны" if there's actually an error or data failed to load
  if (!homePageData && error) {
    return (
      <MainLayout>
        <StateMessage
          title="Данные недоступны"
          message="Не удалось получить информацию о главной странице. Попробуйте обновить страницу позже."
        />
      </MainLayout>
    );
  }

  // Final safety check - this should not happen due to above conditions, but TypeScript needs it
  if (!homePageData) {
    return (
      <MainLayout>
        <StateMessage
          title="Загружаем данные"
          message="Инициализация приложения..."
        />
      </MainLayout>
    );
  }

  const commercial = homePageData.commercialSection;
  const categories = homePageData.company.directions;
  return (
    <MainLayout>
      <section className="w-full">
        <div className="container flex flex-col px-[10px] pb-[20px] pt-[32px] md:px-[32px] md:py-[40px] lg:flex-row lg:gap-[20px] 2xl:px-[180px] 3xl:px-[290px]">
          <div className="flex flex-col gap-[20px] lg:max-w-[581px] lg:gap-[32px]">
            <Typography
              tag="h1"
              className="text-[26px] font-semibold leading-[1] tracking-[-0.03em] md:text-[30px] xl:text-[36px]"
            >
              {commercial.title}
            </Typography>

            <Typography className="font-medium tracking-normal text-[var(--black-grey)]">
              {commercial.description}
            </Typography>

            <div className="flex flex-row items-center justify-between gap-6">
              <Typography className="text-[14px] font-semibold md:text-[16px]">
                {commercial.cta.text}
              </Typography>

              <Button
                endIcon={<LinkArrow className="h-[30px]" />}
                bg={'black'}
                text={commercial.cta.linkButton.text}
                onClick={
                  () => handleBuildingOpen()
                  // handleShowSelectionDownloadModalWindow(
                  //   commercial.cta.linkButton.link
                  // )
                }
                className="h-[38px] gap-[12px] pl-[16px] pr-[4px] text-[16px] md:h-[42px] md:gap-[20px] md:pl-[24px] md:pr-[6px]"
              />
            </div>

            <div className="flex flex-row items-stretch justify-between gap-[29px] sm:justify-start">
              <div className="flex w-fit flex-col gap-[10px]">
                <Typography className="whitespace-nowrap text-[26px] leading-[0.98] tracking-[-0.03em] md:text-[30px] xl:text-[36px]">
                  От {commercial.stats.startArea.value} м2
                </Typography>
                <Typography className="whitespace-nowrap text-[12px] md:text-[14px]">
                  {commercial.stats.startArea.label}
                </Typography>
              </div>

              <div className="w-px border bg-[var(--black-grey)]" />

              <div className="flex w-fit flex-col gap-[10px]">
                <Typography className="whitespace-nowrap text-[26px] leading-[0.98] tracking-[-0.03em] md:text-[30px] xl:text-[36px]">
                  От {commercial.stats.startPrice.value} тыс. ₽
                </Typography>
                <Typography className="whitespace-nowrap text-[12px] md:text-[14px]">
                  {commercial.stats.startPrice.label}
                </Typography>
              </div>
            </div>
            {/* commercial.properties */}
            <VerticalSlider slides={buildings || []} />
          </div>

          <div
            className="
                hidden rounded-[30px] bg-[linear-gradient(rgba(0,0,0,0.2),rgba(0,0,0,0.5)),url('/images/search_widget_bg.png')] bg-cover bg-center bg-no-repeat px-[20px]
                py-[40px]
                lg:flex lg:flex-1 lg:items-end
              "
          >
            <Typography className="border-l pl-[20px] text-[20px] font-semibold leading-[1.3] tracking-normal text-white">
              Полный каталог коммерческой недвижимости в одном удобном доступе
            </Typography>
          </div>
        </div>
      </section>

      <section className="w-full  py-[20px]">
        <div className="container flex flex-col gap-[20px] px-[10px] md:px-[32px] xl:gap-[28px] 2xl:px-[180px] 3xl:px-[290px]">
          <Typography
            tag="h2"
            className="text-[24px] font-medium leading-[0.98] tracking-[-0.03em] xl:text-[26px]"
          >
            Наши направления
          </Typography>

          <div className="flex flex-col gap-[8px] xl:flex-row xl:gap-[20px]">
            {categories.map((item, index) => {
              return (
                <KnowledgeCategoryCard
                  key={index}
                  category={item}
                  handleNavigate={handleBuildingOpen}
                />
              );
            })}
          </div>
        </div>
      </section>

      <TakePresentation />

      <section className="w-full">
        <Map buildings={buildings ? buildings : []} />
      </section>

      <section className="w-full  py-[40px]">
        <div className="container flex flex-col px-[10px] md:px-[32px] 2xl:px-[180px] 3xl:px-[290px]">
          <Feedback />
        </div>
      </section>
    </MainLayout>
  );
}
